import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, CalendarClock, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppointmentCard } from "@/components/livecoach/AppointmentCard";
import { RespondDialog, ThreadDialog } from "@/components/livecoach/LiveCoachDialogs";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CoachLiveCoach() {
  const { user } = useAuth();
  const [appts, setAppts] = useState(null);
  const [avail, setAvail] = useState(null);
  const [roster, setRoster] = useState([]);
  const [tab, setTab] = useState("sessions");
  const [respondTo, setRespondTo] = useState(null);
  const [threadFor, setThreadFor] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/livecoach/appointments/me").then((r) => setAppts(r.data.appointments)).catch((e) => toast.error(formatApiError(e)));
    api.get("/livecoach/availability/me").then((r) => setAvail(r.data)).catch(() => {});
    api.get("/players").then((r) => setRoster(r.data.players || r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (a) => {
    if (!window.confirm("Cancel this Live Coach session?")) return;
    try { await api.post(`/livecoach/appointments/${a.appt_id}/cancel`); toast.success("Cancelled"); load(); }
    catch (er) { toast.error(formatApiError(er)); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="coach-livecoach-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-black text-emerald-400 flex items-center gap-1.5"><Video className="h-3.5 w-3.5" /> BioHacker Live Coach™</div>
          <h1 className="text-2xl font-black text-white mt-1">Real-Time Visual Coaching</h1>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black" data-testid="lc-new-session-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Propose session
        </Button>
      </div>

      <div className="flex gap-2">
        {[["sessions", "Sessions"], ["availability", "Availability"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 py-2 rounded-lg text-sm font-bold ${tab === v ? "bg-white/10 text-white" : "text-white/50 hover:text-white/80"}`} data-testid={`lc-tab-${v}`}>{l}</button>
        ))}
      </div>

      {tab === "sessions" && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="lc-coach-sessions">
          {appts === null ? <div className="text-white/40 animate-pulse">Loading…</div> :
            appts.length === 0 ? <div className="text-white/40 text-sm col-span-2 rounded-xl border border-dashed border-white/15 p-8 text-center"><CalendarClock className="h-6 w-6 mx-auto mb-2 opacity-50" />No sessions yet — propose one to an athlete, or set your availability so athletes can book you.</div> :
              appts.map((a) => <AppointmentCard key={a.appt_id} a={a} meId={user?.user_id} onRespond={setRespondTo} onCancel={cancel} onThread={setThreadFor} />)}
        </div>
      )}

      {tab === "availability" && avail && <AvailabilityEditor avail={avail} setAvail={setAvail} />}

      {respondTo && <RespondDialog appt={respondTo} onClose={() => setRespondTo(null)} onDone={load} />}
      {threadFor && <ThreadDialog appt={threadFor} onClose={() => setThreadFor(null)} />}
      <NewSessionDialog open={newOpen} onClose={() => setNewOpen(false)} roster={roster} onDone={load} />
    </div>
  );
}

const AvailabilityEditor = ({ avail, setAvail }) => {
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setAvail({ ...avail, [k]: v });
  const toggleDay = (day) => {
    const has = avail.windows.some((w) => w.day === day);
    upd("windows", has ? avail.windows.filter((w) => w.day !== day) : [...avail.windows, { day, start: "16:00", end: "20:00" }]);
  };
  const setWin = (day, k, v) => upd("windows", avail.windows.map((w) => (w.day === day ? { ...w, [k]: v } : w)));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/livecoach/availability/me", {
        timezone: avail.timezone, slot_minutes: Number(avail.slot_minutes), buffer_minutes: Number(avail.buffer_minutes || 0),
        min_notice_hours: Number(avail.min_notice_hours), max_advance_days: Number(avail.max_advance_days),
        instant_booking: !!avail.instant_booking, windows: avail.windows,
      });
      setAvail(data); toast.success("Availability saved");
    } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[var(--ink-border)] p-5 max-w-2xl space-y-4" style={{ backgroundColor: "var(--ink-2)" }} data-testid="lc-availability-editor">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <label className="space-y-1"><span className="text-white/50 font-bold">Slot (min)</span><Input type="number" value={avail.slot_minutes} onChange={(e) => upd("slot_minutes", e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="lc-slot-minutes" /></label>
        <label className="space-y-1"><span className="text-white/50 font-bold">Buffer (min)</span><Input type="number" value={avail.buffer_minutes} onChange={(e) => upd("buffer_minutes", e.target.value)} className="bg-white/5 border-white/15 text-white" /></label>
        <label className="space-y-1"><span className="text-white/50 font-bold">Min notice (hrs)</span><Input type="number" value={avail.min_notice_hours} onChange={(e) => upd("min_notice_hours", e.target.value)} className="bg-white/5 border-white/15 text-white" /></label>
        <label className="space-y-1"><span className="text-white/50 font-bold">Max advance (days)</span><Input type="number" value={avail.max_advance_days} onChange={(e) => upd("max_advance_days", e.target.value)} className="bg-white/5 border-white/15 text-white" /></label>
      </div>
      <label className="space-y-1 block text-xs max-w-xs"><span className="text-white/50 font-bold">Timezone (IANA)</span><Input value={avail.timezone} onChange={(e) => upd("timezone", e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="lc-timezone" /></label>
      <div className="flex items-center gap-3">
        <Switch checked={!!avail.instant_booking} onCheckedChange={(v) => upd("instant_booking", v)} data-testid="lc-instant-booking" />
        <span className="text-sm text-white/70">Instant booking — athletes can confirm an open slot without approval</span>
      </div>
      <div className="space-y-2">
        <div className="text-xs font-bold text-white/50">Weekly windows</div>
        {DAYS.map((d, i) => {
          const w = avail.windows.find((x) => x.day === i);
          return (
            <div key={d} className="flex items-center gap-3">
              <button onClick={() => toggleDay(i)} className={`w-14 py-1.5 rounded-lg text-xs font-black ${w ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40" : "bg-white/5 text-white/40 border border-white/10"}`} data-testid={`lc-day-${i}`}>{d}</button>
              {w && <>
                <Input type="time" value={w.start} onChange={(e) => setWin(i, "start", e.target.value)} className="bg-white/5 border-white/15 text-white w-32" />
                <span className="text-white/30 text-xs">to</span>
                <Input type="time" value={w.end} onChange={(e) => setWin(i, "end", e.target.value)} className="bg-white/5 border-white/15 text-white w-32" />
              </>}
            </div>
          );
        })}
      </div>
      <Button onClick={save} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black" data-testid="lc-save-availability">{busy ? "Saving…" : "Save availability"}</Button>
    </div>
  );
};

const NewSessionDialog = ({ open, onClose, roster, onDone }) => {
  const [f, setF] = useState({ athlete_user_id: "", objective: "", duration_minutes: 30, t1: "", t2: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const times = [f.t1, f.t2].filter(Boolean).map((t) => new Date(t).toISOString());
    if (!f.athlete_user_id || !f.objective || times.length === 0) return toast.error("Pick an athlete, objective, and at least one time");
    setBusy(true);
    try {
      await api.post("/livecoach/appointments", {
        athlete_user_id: f.athlete_user_id, objective: f.objective,
        duration_minutes: Number(f.duration_minutes),
        ...(times.length === 1 ? { start_at: times[0] } : { proposed_times: times }),
      });
      toast.success("Session proposed — athlete notified");
      setF({ athlete_user_id: "", objective: "", duration_minutes: 30, t1: "", t2: "" });
      onDone(); onClose();
    } catch (er) { toast.error(formatApiError(er)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--ink-1)] border-[var(--ink-border)] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-black">Propose a Live Coach session</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select value={f.athlete_user_id} onChange={(e) => setF({ ...f, athlete_user_id: e.target.value })} className="w-full rounded-lg bg-white/5 border border-white/15 text-white text-sm p-2.5" data-testid="lc-new-athlete-select">
            <option value="" className="bg-[#0b1424]">Select athlete…</option>
            {roster.map((p) => <option key={p.user_id} value={p.user_id} className="bg-[#0b1424]">{p.name}</option>)}
          </select>
          <Textarea placeholder="Session objective (e.g. Fix late hip rotation on fastballs) *" value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="lc-new-objective" />
          <div className="grid grid-cols-2 gap-3 text-xs">
            <label className="space-y-1"><span className="text-white/50 font-bold">Time option 1 *</span><Input type="datetime-local" value={f.t1} onChange={(e) => setF({ ...f, t1: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="lc-new-time1" /></label>
            <label className="space-y-1"><span className="text-white/50 font-bold">Time option 2</span><Input type="datetime-local" value={f.t2} onChange={(e) => setF({ ...f, t2: e.target.value })} className="bg-white/5 border-white/15 text-white" data-testid="lc-new-time2" /></label>
          </div>
          <label className="space-y-1 block text-xs w-28"><span className="text-white/50 font-bold">Duration (min)</span><Input type="number" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: e.target.value })} className="bg-white/5 border-white/15 text-white" /></label>
          <Button onClick={submit} disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#04121f] font-black" data-testid="lc-new-submit">{busy ? "Sending…" : "Send proposal"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
